import React, { useState, useEffect, useRef } from "react";
import {
  Empty,
  Divider,
  Spin,
  Button,
  Modal,
} from "antd";
import moment from "moment";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import Scrollbar from "react-scrollbars-custom";
import { toast } from "react-toastify";
import PlatemapSample from "./PlatemapSample";
import { fetchInstrumentSequences } from "../../../../../actions/chemistry";
import { getUser, convertUtcToLocal } from "../../../../../utils/helpers";
import TimeFromNow from "../../../../Common/TimeFromNow";
import LoadedSequenceModal from "./LoadedSequenceModal";

const PlatemapSequence = ({
  selectedInstrument,
  view,
  historyDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [modalObj, setModalObj] = useState({ open: false, sequence: null });
  const [viewAfterApi, setViewAfterApi] = useState(view);
  const defaultDeleteModalObj = { open: false, title: "", delete_list: [] };
  const [deleteModalObj, setDeleteModalObj] = useState(defaultDeleteModalObj);
  const defaultFailedModalObj = {
    open: false,
    title: "Failed Sequence Deletions",
    list: [],
  };
  const [failedDeleteData, setFailedDeleteData] = useState(defaultFailedModalObj);
  const abortControllerRef = useRef(null);

  const fetchSequencesApi = async (instrument, generate = false, delete_list = []) => {
    // Abort the previous request if it's still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const date = historyDate ? moment(historyDate.$d).format("MM/DD/YY") : "";
    setSelectedSequence(null);
    if (instrument) {
      setLoading(true);
      const payload = {
        instrument_id: instrument.id,
        user_id: getUser()?.id,
        generate,
        date,
        delete_list,
      };
      // Create a new AbortController for the new fetch request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const {
        success, message, result, failed_deletions,
      } = await fetchInstrumentSequences(payload, abortController.signal);
      if (success) {
        setSequences(result);
        if (failed_deletions.length > 0) {
          setFailedDeleteData((prev) => {
            const newState = { ...prev, open: true, list: failed_deletions };
            return newState;
          });
        }
      } else if (!abortController.signal.aborted) {
        toast.error(message);
        setSequences([]);
      }
      setLoading(abortController.signal.aborted);
    } else {
      setSequences([]);
    }
    setViewAfterApi(view);
  };

  useEffect(() => {
    fetchSequencesApi(selectedInstrument);
  }, [selectedInstrument, view, historyDate]); // eslint-disable-line

  const handleSequenceOnclick = (item) => {
    if (selectedSequence && selectedSequence.sequence_id === item.sequence_id) {
      setSelectedSequence(null);
    } else {
      setSelectedSequence(item);
    }
  };

  const handleLoadedOnClick = (sequence) => {
    setModalObj({ open: true, sequence });
  };

  const handleOpenDeleteModal = (e, list, type) => {
    e.stopPropagation();
    const delete_list = list.map((item) => item.sequence_id);
    setDeleteModalObj({
      open: true,
      title: `Are you sure you want to delete ${type === "all" ? "all sequences" : list[0].sequence_id}?`,
      delete_list,
    });
  };

  const handleDeleteSequences = () => {
    const list = deleteModalObj.delete_list;
    setDeleteModalObj(defaultDeleteModalObj);
    fetchSequencesApi(selectedInstrument, false, list);
  };

  const showTimeFromNow = viewAfterApi === "Pending" && view === "Pending" && sequences.length > 0;
  const showDeleteGenerate = viewAfterApi === "Pending" && view === "Pending";
  return (
    <div className="PlatemapSequenceContainer">
      <div className="PlatemapSection">
        <div className="PlatemapSection__Header">
          <div className="PlatemapSection__HeaderContent">
            <div className="PlatemapSection__HeaderContent__Info">
              <h5>Sequences</h5>
              {showTimeFromNow && (<TimeFromNow prefix="Generated " time={convertUtcToLocal(sequences[0].generated_at)} dateFormat="DD MMM YYYY" />)}
            </div>
            {showDeleteGenerate && (
            <div className="PlatemapSequence__Buttons">
              <Button disabled={loading || !selectedInstrument || sequences.length === 0} onClick={(e) => handleOpenDeleteModal(e, sequences, "all")} icon={<DeleteOutlined />}>Delete</Button>
              <Button disabled={loading || !selectedInstrument} onClick={() => fetchSequencesApi(selectedInstrument, true)} icon={<ReloadOutlined />}>Generate</Button>
            </div>
            )}
          </div>
          <Divider className="PlatemapDivider" />
        </div>
        <Spin wrapperClassName="Platemap__Spin" spinning={loading} size="large">
          {sequences.length > 0 && (
          <Scrollbar
            className="PlatemapItemScrollBar"
            disableTrackYWidthCompensation
            disableTracksWidthCompensation
          >
            <div className="PlatemapSection__Items__Container">
              {sequences.map((item, index) => (
                <div key={index} className="Platemap__SequenceItemContainer">
                  <div key={index} className={`PlatemapSection__Item${item.sequence_id === selectedSequence?.sequence_id ? "--selected" : ""}`} onClick={() => handleSequenceOnclick(item)}>
                    {item.sequence_id}
                    {showDeleteGenerate && (<Button onClick={(e) => handleOpenDeleteModal(e, [item], "current")} type="text" icon={<DeleteOutlined />} disabled={loading} />)}
                  </div>
                  {viewAfterApi === "Pending" && (<Button onClick={() => handleLoadedOnClick(item)}>Loaded</Button>)}
                </div>
              ))}
            </div>
          </Scrollbar>
          )}
          {sequences.length === 0 && (<Empty className="PlatemapEmpty" image={Empty.PRESENTED_IMAGE_SIMPLE} />)}
        </Spin>
      </div>
      <PlatemapSample selectedSequence={selectedSequence} />
      <LoadedSequenceModal modalObj={modalObj} setModalObj={setModalObj} fetchSequencesApi={fetchSequencesApi} selectedInstrument={selectedInstrument} />
      <Modal
        centered
        closable={false}
        maskClosable={false}
        title={deleteModalObj.title}
        open={deleteModalObj.open}
        onCancel={() => setDeleteModalObj(defaultDeleteModalObj)}
        onOk={handleDeleteSequences}
        width="450px"
        okText="Confirm"
        cancelText="Close"
      />
      <Modal
        centered
        closable
        maskClosable={false}
        title={failedDeleteData.title}
        open={failedDeleteData.open}
        onCancel={() => setFailedDeleteData(defaultFailedModalObj)}
        width="450px"
        okText="Confirm"
        cancelText="Close"
        footer={[]}
      >
        <span>{failedDeleteData.list.join(", ")}</span>
      </Modal>
    </div>
  );
};

export default PlatemapSequence;
